import { prontoLoginUrl } from '../../utils/ConstantUtils'
import { updateTokenUsageTime } from '../../utils/storageUtils'
import { getApiInstance } from '../axiosInterceptor'

const IDLE_EXPIRATION_TIME = 30 * 60 // 30 minutes in seconds

/**
 * Generates or retrieves a Pronto token for API authentication
 * @param {object} instance - MSAL instance for authentication
 * @param {boolean} isCognitoUser - Flag indicating if the user is authenticated with Cognito
 * @returns {string|null} - The authentication token or null if failed
 */
export const generateProntoToken = async (instance) => {
  try {
    // Try to get token from session storage first
    const tokenInSession = getTokenFromSession()
    if (tokenInSession) {
      return tokenInSession
    }

    const response = await getApiInstance(instance).get(prontoLoginUrl)
    if (response.status === 200 && response.data && response.data.accessToken) {
      const tokenValue = response.data.accessToken
      const timeWhenGenerated = new Date().getTime() / 1000

      // Store token in session storage
      sessionStorage.setItem(
        'tokenData',
        JSON.stringify({ tokenValue, timeWhenGenerated })
      )
      sessionStorage.setItem('lastUsedTime', timeWhenGenerated)

      return tokenValue
    } else {
      throw new Error('Invalid Pronto token response')
    }
  } catch (error) {
    throw new Error('Failed to connect to pronto', error)
  }
}

/**
 * Retrieves a valid token from session storage if available
 * @returns {string|null} - The token or null if not available/expired
 */
const getTokenFromSession = () => {
  try {
    const tokenDataStr = sessionStorage.getItem('tokenData')
    if (!tokenDataStr) return null

    const tokenData = JSON.parse(tokenDataStr)
    const lastUsedTime = parseFloat(sessionStorage.getItem('lastUsedTime') || '0')

    if (tokenData) {
      const { tokenValue } = tokenData
      const currentTime = new Date().getTime() / 1000

      if ((currentTime - lastUsedTime) < IDLE_EXPIRATION_TIME) {
        updateTokenUsageTime()
        return tokenValue // Token is valid
      } else {
        // Token expired, remove it
        sessionStorage.removeItem('tokenData')
        sessionStorage.removeItem('lastUsedTime')
      }
    }
  } catch {
    // In case of any parsing errors, clear the storage
    sessionStorage.removeItem('tokenData')
    sessionStorage.removeItem('lastUsedTime')
  }
  return null // No valid token
}